import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useParams } from 'react-router';

import { arrayHasLength, lastItemArray } from '~utils/array';
import { SCHEDULED_DATE_AVAILABILITY } from '~redux/SearchStore/constants';
import { TARGETS as orderTargets } from '~redux/Order/constants';
import useCheckStoreOpen from '~hooks/useCheckStoreOpen';
import { noHoursAvailable } from '~redux/SearchStore/utils';

import { getSelectedDateTime, mapDateToOptions } from '../utils';

const useFutureOrderModal = () => {
  const dispatch = useDispatch();
  const { name } = useParams();
  const id = name ? lastItemArray(name.split('-')) : 0;
  const { menu = [] } = useSelector((state) => state.menu);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [dateOptions, setDateOptions] = useState([]);
  const [parsedOptions, setParsedOptions] = useState([]);
  const [isScheduled, setIsScheduled] = useState(false);
  const scheduledDateAvailability = useSelector((state) => state.searchStore[SCHEDULED_DATE_AVAILABILITY]);
  const futureOrder = useSelector((state) => state.order[orderTargets.FUTURE_ORDER]);
  const preselectedFutureOrder = useSelector((state) => state.order[orderTargets.PRESELECTED_DATE]);
  const { onlyScheduledOrders } = useCheckStoreOpen();
  const unavailableFutureHours = futureOrder && noHoursAvailable(scheduledDateAvailability, futureOrder);

  useEffect(() => {
    if (scheduledDateAvailability) {
      setDateOptions(mapDateToOptions(scheduledDateAvailability));
    }
  }, [scheduledDateAvailability]);

  useEffect(() => {
    if (preselectedFutureOrder) {
      const nextAvailableDay = Object.keys(dateOptions).find((date) =>
        moment(date, 'YYYY-MM-DD').isAfter(moment(preselectedFutureOrder.date, 'YYYY-MM-DD'))
      );

      const selectedDateTime = dateOptions[preselectedFutureOrder.date]
        ? getSelectedDateTime({
            dateOptions,
            preselectedFutureOrder,
            nextAvailableDay
          })
        : {
            date: nextAvailableDay,
            time: dateOptions[nextAvailableDay].times[0].label
          };

      if (selectedDateTime.date && selectedDateTime.time) {
        setSelectedDate(selectedDateTime.date);
        setSelectedTime(selectedDateTime.time);
        setIsScheduled(true);
        return;
      }
    }

    if (futureOrder?.date && futureOrder?.time) {
      setSelectedDate(futureOrder?.date);
      setSelectedTime(futureOrder?.time);
      setIsScheduled(true);
      return;
    }

    if (parsedOptions.length > 0) {
      setSelectedDate(parsedOptions[0].value);
      setSelectedTime(parsedOptions[0].times[0].label);
    }
  }, [dateOptions, dispatch, futureOrder, parsedOptions, preselectedFutureOrder]);

  useEffect(() => {
    setParsedOptions(Object.keys(dateOptions).reduce((ac, el) => [...ac, dateOptions[el]], []));
  }, [dateOptions]);

  useEffect(() => {
    if (onlyScheduledOrders) {
      setIsScheduled(true);
    }
  }, [onlyScheduledOrders]);

  useEffect(() => {
    if (unavailableFutureHours && parsedOptions) {
      setSelectedDate(parsedOptions[0]?.value);
      setSelectedTime(dateOptions[selectedDate]?.times[0].label);
    }
  }, [unavailableFutureHours, parsedOptions, dateOptions, selectedDate]);

  const handleSelectHours = (e) => {
    setSelectedDate(e.target.value);
    const timeAvailableOnNewDate = dateOptions[e.target.value].times.some(
      (time) => time.label === selectedTime
    );
    if (!timeAvailableOnNewDate) {
      setSelectedTime(dateOptions[e.target.value].times[0].label);
    }
  };
  const handleSelectTime = (e) => setSelectedTime(e.target.value);
  const timeOptions = dateOptions[selectedDate]?.times;

  const timeIsAvailable = isScheduled && arrayHasLength(Object.keys(dateOptions)) && selectedDate;
  const showCancelBtn = !!preselectedFutureOrder;

  const shouldOpenProductDetails = menu
    .flatMap((category) => category.prices)
    .some((p) => p.product.id === parseInt(id));

  return {
    handleSelectHours,
    handleSelectTime,
    unavailableFutureHours,
    selectedTime,
    selectedDate,
    parsedOptions,
    timeOptions,
    timeIsAvailable,
    setIsScheduled,
    isScheduled,
    showCancelBtn,
    shouldOpenProductDetails
  };
};

export default useFutureOrderModal;
